import React from "react";
import type { TopNavMenuItemDto } from "@lib/ShiOneClient";
import type { KeyboardHandlerDeps } from "../types/topNavTypes";

export function createTopButtonKeyDown(
  deps: KeyboardHandlerDeps,
  sortedTopNavItems: TopNavMenuItemDto[],
) {
  return (
    e: React.KeyboardEvent<HTMLButtonElement>,
    item: TopNavMenuItemDto,
    index: number,
  ) => {
    switch (e.key) {
      case "ArrowRight": {
        e.preventDefault();
        const nextIndex = (index + 1) % sortedTopNavItems.length;
        const nextItem = sortedTopNavItems[nextIndex];
        if (nextItem?.menuId != null) {
          deps.topButtonRefs.current.get(nextItem.menuId)?.focus();
        }
        break;
      }
      case "ArrowLeft": {
        e.preventDefault();
        const prevIndex =
          (index - 1 + sortedTopNavItems.length) % sortedTopNavItems.length;
        const prevItem = sortedTopNavItems[prevIndex];
        if (prevItem?.menuId != null) {
          deps.topButtonRefs.current.get(prevItem.menuId)?.focus();
        }
        break;
      }
      case "ArrowDown":
      case "Enter":
      case " ": {
        e.preventDefault();
        deps.openedViaKeyboard.current = true;
        deps.setAnchorEl(e.currentTarget);
        deps.setParentButtonSelected(item.menuId ?? null);
        deps.setCategorySelected(null);
        deps.setCategoryAnchorEl(null);
        break;
      }
    }
  };
}

export function createCategoryKeyDown(deps: KeyboardHandlerDeps) {
  return (
    e: React.KeyboardEvent<HTMLElement>,
    category: TopNavMenuItemDto,
    categories: TopNavMenuItemDto[],
    index: number,
  ) => {
    switch (e.key) {
      case "Tab": {
        e.preventDefault();
        if (e.shiftKey) {
          if (index > 0) {
            const prevItem = categories[index - 1];
            if (prevItem?.menuId != null) {
              deps.categoryItemRefs.current.get(prevItem.menuId)?.focus();
            }
          } else {
            const triggerButton =
              deps.parentButtonSelected != null
                ? deps.topButtonRefs.current.get(deps.parentButtonSelected)
                : null;
            deps.closeMenu();
            triggerButton?.focus();
          }
        } else {
          if (index < categories.length - 1) {
            const nextItem = categories[index + 1];
            if (nextItem?.menuId != null) {
              deps.categoryItemRefs.current.get(nextItem.menuId)?.focus();
            }
          } else {
            deps.closeMenu();
          }
        }
        deps.setCategorySelected(null);
        break;
      }
      case "Enter": {
        const hasChildren = (category.children?.length ?? 0) > 0;
        if (hasChildren) {
          e.preventDefault();
          deps.setCategorySelected(category.menuId ?? null);
          deps.setCategoryAnchorEl(e.currentTarget);
          const firstChild = category.children?.[0];
          if (firstChild?.menuId != null) {
            setTimeout(() => {
              deps.subcategoryItemRefs.current
                .get(firstChild.menuId!)
                ?.focus();
            }, 100);
          }
        }
        break;
      }
      case "ArrowDown": {
        e.preventDefault();
        const nextIndex = (index + 1) % categories.length;
        const nextItem = categories[nextIndex];
        if (nextItem?.menuId != null) {
          deps.categoryItemRefs.current.get(nextItem.menuId)?.focus();
        }
        deps.setCategorySelected(null);
        break;
      }
      case "ArrowUp": {
        e.preventDefault();
        const prevIndex = (index - 1 + categories.length) % categories.length;
        const prevItem = categories[prevIndex];
        if (prevItem?.menuId != null) {
          deps.categoryItemRefs.current.get(prevItem.menuId)?.focus();
        }
        deps.setCategorySelected(null);
        break;
      }
      case "ArrowRight": {
        e.preventDefault();
        const hasChildren = (category.children?.length ?? 0) > 0;
        if (hasChildren) {
          deps.setCategorySelected(category.menuId ?? null);
          deps.setCategoryAnchorEl(e.currentTarget);
          const firstChild = category.children?.[0];
          if (firstChild?.menuId != null) {
            setTimeout(() => {
              deps.subcategoryItemRefs.current
                .get(firstChild.menuId!)
                ?.focus();
            }, 100);
          }
        }
        break;
      }
      case "Escape": {
        e.preventDefault();
        e.stopPropagation();
        const triggerButton =
          deps.parentButtonSelected != null
            ? deps.topButtonRefs.current.get(deps.parentButtonSelected)
            : null;
        deps.closeMenu();
        triggerButton?.focus();
        break;
      }
    }
  };
}

export function createSubcategoryKeyDown(deps: KeyboardHandlerDeps) {
  return (
    e: React.KeyboardEvent<HTMLElement>,
    subcategories: TopNavMenuItemDto[],
    index: number,
  ) => {
    switch (e.key) {
      case "Tab": {
        e.preventDefault();
        if (e.shiftKey) {
          if (index > 0) {
            const prevItem = subcategories[index - 1];
            if (prevItem?.menuId != null) {
              deps.subcategoryItemRefs.current
                .get(prevItem.menuId)
                ?.focus();
            }
          } else {
            if (deps.categorySelected != null) {
              deps.categoryItemRefs.current
                .get(deps.categorySelected)
                ?.focus();
              deps.setCategorySelected(null);
            }
          }
        } else {
          if (index < subcategories.length - 1) {
            const nextItem = subcategories[index + 1];
            if (nextItem?.menuId != null) {
              deps.subcategoryItemRefs.current
                .get(nextItem.menuId)
                ?.focus();
            }
          } else {
            if (deps.categorySelected != null) {
              deps.categoryItemRefs.current
                .get(deps.categorySelected)
                ?.focus();
              deps.setCategorySelected(null);
            }
          }
        }
        break;
      }
      case "Enter": {
        break;
      }
      case "ArrowDown": {
        e.preventDefault();
        const nextIndex = (index + 1) % subcategories.length;
        const nextItem = subcategories[nextIndex];
        if (nextItem?.menuId != null) {
          deps.subcategoryItemRefs.current.get(nextItem.menuId)?.focus();
        }
        break;
      }
      case "ArrowUp": {
        e.preventDefault();
        const prevIndex =
          (index - 1 + subcategories.length) % subcategories.length;
        const prevItem = subcategories[prevIndex];
        if (prevItem?.menuId != null) {
          deps.subcategoryItemRefs.current.get(prevItem.menuId)?.focus();
        }
        break;
      }
      case "ArrowLeft": {
        e.preventDefault();
        if (deps.categorySelected != null) {
          deps.categoryItemRefs.current
            .get(deps.categorySelected)
            ?.focus();
          deps.setCategorySelected(null);
        }
        break;
      }
      case "Escape": {
        e.preventDefault();
        e.stopPropagation();
        if (deps.categorySelected != null) {
          deps.categoryItemRefs.current
            .get(deps.categorySelected)
            ?.focus();
          deps.setCategorySelected(null);
        }
        break;
      }
    }
  };
}

